import { Cloud } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useState } from "react";
import { EnvironmentViewModel, UserClient } from "../../api/api";
import ListWithFilter from "../../common/components/ListWithFilter";
import { useApiClient, useToastService } from "../../common/hooks";

interface EnvironmentSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onEnvironmentChanged: (environment: EnvironmentViewModel) => void;
  availableEnvironments?: EnvironmentViewModel[];
}

const EnvironmentSelectorDialog: React.FC<EnvironmentSelectorDialogProps> = ({ open, onClose, onEnvironmentChanged, availableEnvironments }) => {
  const [environments, setEnvironments] = useState<EnvironmentViewModel[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const userClient = useApiClient(UserClient);
  const toast = useToastService();

  const loadEnvironments = React.useCallback(async () => {
    // If availableEnvironments are provided, use them instead of fetching
    if (availableEnvironments) {
      setEnvironments(availableEnvironments);
      return;
    }

    // Otherwise, fetch from API (fallback for backwards compatibility)
    setIsLoading(true);
    try {
      const userData = await userClient.getUser();
      setEnvironments(userData.environments || []);
    } catch (error) {
      toast.error("Failed to load environments");
      console.error("Error loading environments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userClient, toast, availableEnvironments]);

  useEffect(() => {
    if (open) {
      loadEnvironments();
    }
  }, [open, loadEnvironments]);

  const handleEnvironmentSelect = (environment: EnvironmentViewModel) => {
    setSelectedEnvironmentId(environment.id);
  };

  const handleConfirm = async () => {
    if (!selectedEnvironmentId) {
      toast.info("Please select an environment");
      return;
    }

    const selectedEnvironment = environments.find((e) => e.id === selectedEnvironmentId);
    if (!selectedEnvironment) {
      toast.error("Selected environment not found");
      return;
    }

    setIsSaving(true);
    try {
      await userClient.setEnvironment(selectedEnvironmentId);
      toast.success("Environment changed successfully");
      onEnvironmentChanged(selectedEnvironment);
      onClose();
    } catch (error) {
      toast.error("Failed to change environment");
      console.error("Error changing environment:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setSelectedEnvironmentId(undefined);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Environment</DialogTitle>
      <DialogContent>
        <ListWithFilter
          items={environments}
          isLoading={isLoading}
          selectedItemId={selectedEnvironmentId}
          onSelected={handleEnvironmentSelect}
          placeholder="Search environments..."
          displayNameProperty="code"
          keyProperty="id"
          maxHeight="400px"
          emptyMessage="No environments available"
          icon={() => <Cloud />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selectedEnvironmentId || isSaving}>
          {isSaving ? "Changing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnvironmentSelectorDialog;
